
import React, { useState, useEffect } from 'react';
import HeroSection from '../../components/HeroSection/HeroSection';
import TourSection from '../../components/TourSection/TourSection';
import { tourService } from '../../services/api';
import './Home.css';

// ContactForm component for inquiry email (must be defined before Home)
function ContactForm() {
  const [form, setForm] = React.useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSent(false);
    try {
      const res = await fetch('https://travel-agency-backend-travel.onrender.com/api/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setSent(true);
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setError(data.error || 'Failed to send.');
      }
    } catch (err) {
      setError('Failed to send.');
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <input
          type="text"
          name="name"
          placeholder="Your Name"
          value={form.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <input
          type="email"
          name="email"
          placeholder="Your Email"
          value={form.email}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <input
          type="text"
          name="subject"
          placeholder="Subject"
          value={form.subject}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <textarea
          name="message"
          placeholder="Your Message"
          rows="5"
          value={form.message}
          onChange={handleChange}
          required
        ></textarea>
      </div>
      <button type="submit" className="submit-btn" disabled={sending}>
        {sending ? 'Sending...' : 'Send Message'}
      </button>
      {(sent || error) && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
          {sent && <div style={{ fontSize: '30px', color: '#006400', textAlign: 'center' }}>Email sent successfully!</div>}
          {error && <div style={{ fontSize: '30x', color: 'red', textAlign: 'center' }}>{error}</div>}
        </div>
      )}
    </form>
  );
}

const Home = () => {
  const [toursData, setToursData] = useState({
    popular: [],
    worldwide: [],
    india: [],
    honneymoon: [],
    family: []
  });
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    fetchAllTours();
  }, []);

  const fetchAllTours = async () => {
    try {
      setLoading(true);
      const data = await tourService.getAllTours();
      setToursData({
        popular: data.popular || [],
        worldwide: data.uk || [],
        india: data.india || [],
        honneymoon: data.eurostar || [],
        family: data.faraway || []
      });
    } catch (error) {
      console.error('Failed to fetch tours:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    try {
      setLoading(true);
      const results = await tourService.searchTours(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
  };

  if (loading && !toursData.popular.length) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading amazing destinations...</p>
      </div>
    );
  }

  // Gather all tour names for autocomplete
  const allTourNames = [
    ...toursData.popular,
    ...toursData.worldwide,
    ...toursData.india,
    ...toursData.honneymoon,
    ...toursData.family
  ].map(t => t.name).filter(Boolean);

  return (
    <div className="home-page">
      <HeroSection onSearch={handleSearch} allTourNames={allTourNames} />

      {searchResults ? (
        <section className="search-results">
          <div className="container">
            <div className="search-header">
              <h2>Search Results ({searchResults.length})</h2>
              <button onClick={clearSearch} className="clear-search-btn">
                ✕ Clear Search
              </button>
            </div>
            <div className="tours-grid">
              {searchResults.length > 0 ? (
                searchResults.map((tour) => (
                  <div key={tour.id}>
                    <TourSection tours={[tour]} />
                  </div>
                ))
              ) : (
                <p className="no-results">No tours found matching your search.</p>
              )}
            </div>
          </div>
        </section>
      ) : (
        <>
          <TourSection
            id="popular-tours"
            title="Popular Tours"
            tours={toursData.popular}
            category="popular"
            viewAllLink="/holidays/popular"
          />

          <TourSection
            id="worldwide-tours"
            title="WorldWide Tours"
            tours={toursData.worldwide}
            category="worldwide"
            viewAllLink="/holidays/worldwide"
          />

          <TourSection
            id="india-tours"
            title="India Tours"
            tours={toursData.india}
            category="india"
            viewAllLink="/holidays/india"
          />

          <TourSection
            id="honneymoon-packages"
            title="Honneymoon Packages"
            tours={toursData.honneymoon}
            category="honneymoon"
            viewAllLink="/holidays/honneymoon"
          />

          <TourSection
            id="family-packages"
            title="Family Packages"
            tours={toursData.family}
            category="family"
            viewAllLink="/holidays/family"
          />

          <section id="about-section" className="about-section">
            <div className="container">
              <h2>About Oyatri</h2>
              <p>
                We believe that touring should be fun and that our guests should be well-versed
                in the history and aspects of culture, whilst they explore the world. Our tours
                are designed for travellers to enjoy a diverse array of fascinating countries.
              </p>
              <div className="about-grid">
                {/* <div className="about-card">
                  <div className="about-icon">🏆</div>
                  <h3>Award Winning</h3>
                  <p>Recognized for excellence in travel services</p>
                </div> */}
                <div className="about-card">
                  <div className="about-icon">✈️</div>
                  <h3>Experienced</h3>
                  <p>Over 20 years of travel expertise</p>
                </div>
                <div className="about-card">
                  <div className="about-icon">🌍</div>
                  <h3>Global Reach</h3>
                  <p>500+ destinations worldwide</p>
                </div>
              </div>
            </div>
          </section>

          <section id="info-section" className="info-section">
            <div className="container">
              <div className="info-grid">
                

                <div className="info-card">
                  <div className="info-icon">🛂</div>
                  <h3>Need a visa? We can help</h3>
                  <p>Expert visa assistance for all your travel destinations</p>
                  {/* <a href="javascript:void(0)" className="info-link">Know More →</a> */}
                </div>

                <div className="info-card">
                  <div className="info-icon">📖</div>
                  <h3>How to book your trip</h3>
                  <p>Simple and secure booking process with flexible payment options</p>
                    {/* <a href="javascript:void(0)" className="info-link">Know More →</a> */}
                </div>
              </div>
            </div>
          </section>

          <section id="contact-section" className="contact-section">
            <div className="container">
              <h2>Get In Touch</h2>
              <div className="contact-content">
                <div className="contact-info">
                  <div className="contact-item">
                    <div className="contact-icon"><i className="fas fa-phone-alt" style={{ color: 'brown' }}></i></div>
                    <div>
                      <h3>Phone</h3>
                      <p>+91 96256 53930</p>
                      <p className="small">Available Mon-Sun, 9AM-6PM</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon"><i className="fas fa-envelope" style={{ color: 'brown' }}></i></div>
                    <div>
                      <h3>Email</h3>
                      <p>oyatriletsgo@gmail.com</p>
                      <p className="small">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon"><i className="fas fa-map-marker-alt" style={{ color: 'brown' }}></i></div>
                    <div>
                      <h3>Address</h3>
                      <p>Gali Number 25 & Gali Number 23, Chandan Vihar, New Delhi 110084, Delhi, India</p>
                      <p className="small">Office open for visits</p>
                    </div>
                  </div>

                  {/* <div className="contact-item">
                    <div className="contact-icon"><i className="fab fa-whatsapp" style={{ color: 'green' }}></i></div>
                    <div>
                      <h3>WhatsApp</h3>
                      <p>+91 96256 53930</p>
                      <p className="small">Quick support available</p>
                    </div>
                  </div> */}
                </div>


                {/* Contact form with AJAX email sending */}
                <ContactForm />
              </div>
            </div>
          </section>
        </>
      )}
      <WhatsAppButton />
    </div>
  );
};

export default Home;

// WhatsApp floating button CSS
const whatsappBtnStyle = {
  position: 'fixed',
  bottom: '30px',
  right: '30px',
  zIndex: 1000,
  background: 'green',
  borderRadius: '50%',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  width: '60px',
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

function WhatsAppButton() {
  const message = encodeURIComponent('Hello I want to explore the packages you have.');
  return (
    <a
      href={`https://wa.me/919625653930?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      style={whatsappBtnStyle}
      aria-label="Chat on WhatsApp"
    >
      <i className="fab fa-whatsapp" style={{ color: 'white', fontSize: 40 }}></i>
    </a>
  );
}

